// src/components/configuration/sections/CommunicationsSectionShadcn.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  AlertCircle
} from 'lucide-react';

// shadcn components
import { Alert, AlertDescription } from '../../ui/alert';
import { Spinner } from '../../ui/spinner';

// Keep your existing PageContainer for headers
import PageContainer from '../../ui/layout/PageContainer/PageContainer';

// Import the shadcn split panel components
import CommunicationsTemplatesListShadcn from '../../ui/sections/CommunicationsTemplatesList/CommunicationsTemplatesListShadcn';
import CommunicationsTemplateEditorShadcn from '../../ui/sections/CommunicationsTemplateEditor/CommunicationsTemplateEditorShadcn';

// Import the Settings context for real data
import { useSettings } from '../../../hooks/useSettings';

// Import template utilities
import { createTempTemplate, generateDefaultContent } from '../../../utils/templateDefaults';

const CommunicationsSectionShadcn = () => {
  // 🔗 REAL PIPELINE CONNECTION - Get real data from settings context
  const {
    // Connection state
    loading: settingsLoading,
    error,
    isConnected,

    // Template data (REAL DATA!)
    emailTemplates,
    smsTemplates,
    savingTemplates,

    // Template operations (REAL METHODS!)
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
    createSMSTemplate,
    updateSMSTemplate,
    deleteSMSTemplate,
    testTemplate,

    // Utility methods
    getEmailTemplateById,
    getSMSTemplateById
  } = useSettings();

  // Local UI state for split panel
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [templateType, setTemplateType] = useState('email'); // 'email' or 'sms'
  const [showInactive, setShowInactive] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // NEW: Temporary template state for inline creation
  const [tempTemplate, setTempTemplate] = useState(null);
  const [creatingMode, setCreatingMode] = useState(false);

  // Compose real and temporary templates
  const allTemplates = useMemo(() => {
    const realTemplates = templateType === 'email' ? emailTemplates : smsTemplates;
    return tempTemplate ? [...realTemplates, tempTemplate] : realTemplates;
  }, [emailTemplates, smsTemplates, tempTemplate, templateType]);

  // Enhanced template resolution (handles both real and temporary)
  const selectedTemplate = selectedTemplateId ?
    (tempTemplate?.id === selectedTemplateId ?
      tempTemplate :
      (templateType === 'email' ?
        getEmailTemplateById(selectedTemplateId) :
        getSMSTemplateById(selectedTemplateId)
      )
    ) : null;

  // Auto-select first template when templates load
  useEffect(() => {
    const currentTemplates = allTemplates;
    if (currentTemplates && currentTemplates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(currentTemplates[0].id);
    }
  }, [templateType, allTemplates, selectedTemplateId]);

  // Handle template selection when switching types + cleanup temp templates
  useEffect(() => {
    // Clear temporary template when switching types
    if (tempTemplate) {
      setTempTemplate(null);
      setCreatingMode(false);
    }

    const currentTemplates = allTemplates;
    if (currentTemplates && currentTemplates.length > 0) {
      // Check if current selection is valid for new type
      const templateExists = currentTemplates.find(t => t.id === selectedTemplateId);
      if (!templateExists) {
        setSelectedTemplateId(currentTemplates[0].id);
      }
    } else {
      setSelectedTemplateId(null);
    }
  }, [templateType, emailTemplates, smsTemplates]); // Note: don't include allTemplates to avoid loops

  // 🚀 REAL TEMPLATE OPERATIONS - Connected to actual pipeline!
  const handleUpdateTemplate = async (templateId, updateData) => {
    try {
      console.log(`📧 Updating ${templateType} template:`, templateId, updateData);

      // Call real pipeline methods
      if (templateType === 'email') {
        await updateEmailTemplate(templateId, updateData);
      } else {
        await updateSMSTemplate(templateId, updateData);
      }

      setSuccessMessage(`${templateType === 'email' ? 'Email' : 'SMS'} template updated successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ Failed to update template:', error);
      // Error handling is managed by the provider
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      console.log(`🗑️ Deleting ${templateType} template:`, templateId);

      // Call real pipeline methods
      if (templateType === 'email') {
        await deleteEmailTemplate(templateId);
      } else {
        await deleteSMSTemplate(templateId);
      }

      setSuccessMessage('Template deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Clear selection if deleted template was selected
      if (selectedTemplateId === templateId) {
        setSelectedTemplateId(null);
      }
    } catch (error) {
      console.error('❌ Failed to delete template:', error);
      // Error handling is managed by the provider
    }
  };

  const handleCreateTemplate = async (templateData) => {
    try {
      console.log(`📧 Creating new ${templateType} template:`, templateData);

      // Call real pipeline methods
      if (templateType === 'email') {
        await createEmailTemplate(templateData);
      } else {
        await createSMSTemplate(templateData);
      }

      setSuccessMessage(`${templateType === 'email' ? 'Email' : 'SMS'} template created successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ Failed to create template:', error);
      // Error handling is managed by the provider
    }
  };

  // NEW: Handle creating temporary template (inline creation)
  const handleCreateNewTemplate = () => {
    console.log(`🎨 Creating new temporary ${templateType} template`);

    const temp = createTempTemplate(templateType);
    setTempTemplate(temp);
    setSelectedTemplateId(temp.id); // Auto-select the temp template
    setCreatingMode(true);
  };

  // NEW: Handle converting temporary template to real template
  const handleCreateRealTemplate = async (tempTemplateData) => {
    try {
      console.log(`📧 Converting temporary template to real ${templateType} template:`, tempTemplateData);

      // Extract real template data (remove temporary fields)
      const { isTemporary, id, ...realTemplateData } = tempTemplateData;

      // Create via existing pipeline
      if (templateType === 'email') {
        await createEmailTemplate(realTemplateData);
      } else {
        await createSMSTemplate(realTemplateData);
      }

      // Clear temporary state
      setTempTemplate(null);
      setCreatingMode(false);

      setSuccessMessage(`${templateType === 'email' ? 'Email' : 'SMS'} template created successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);

      // Note: Real template will auto-appear in list and potentially get auto-selected
      // via existing provider event handling

    } catch (error) {
      console.error('❌ Failed to create real template:', error);
      // Keep temporary template and creation mode on error
      // Error handling is managed by the provider
    }
  };

  // NEW: Handle trigger selection for temporary templates
  const handleTriggerChange = (triggerKey) => {
    if (tempTemplate && triggerKey) {
      console.log(`🎯 Setting trigger "${triggerKey}" for temporary template`);

      const defaults = generateDefaultContent(triggerKey, templateType);
      setTempTemplate(prev => ({
        ...prev,
        template_key: triggerKey,
        ...defaults
      }));
    }
  };

  const handleTestTemplate = async (templateId) => {
    try {
        console.log(`🧪 Testing ${templateType} template:`, selectedTemplate?.name);

        // Call real pipeline method
        await testTemplate(templateId, templateType, {
        // Test data can be customized based on template type
        customer_name: 'John Doe',
        service_type: 'Pool Inspection',
        appointment_date: 'Tomorrow',
        appointment_time: '10:00 AM',
        service_address: '123 Main St'
        });

        setSuccessMessage(`Test ${templateType === 'email' ? 'email' : 'SMS'} sent successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
        console.error('❌ Failed to test template:', error);
        // Error handling is managed by the provider
    }
  };

  // Handle connection issues
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Connection Issue</p>
                <p className="text-sm">Unable to connect to settings service. Please check your connection.</p>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  // Loading state - show loading while settings are loading
  if (settingsLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Spinner className="size-16 text-primary mx-auto mb-4" />
            <p className="text-foreground text-lg">Loading communication templates...</p>
            <p className="text-muted-foreground text-sm mt-2">Retrieving email and SMS templates</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Handle errors
  if (error) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Settings Error</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer heightMode="viewport">
      {/* Success Message */}
      {successMessage && (
        <Alert className="mb-4 flex-shrink-0 bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
          <AlertDescription>
            {successMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert variant="destructive" className="mb-4 flex-shrink-0">
          <AlertDescription>
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content - Split Panel Layout */}
      <div className="grid grid-cols-12 gap-4 h-full">
        {/* Left Panel - Templates List (40% width) */}
        <div className="col-span-4 h-full min-h-0">
          <CommunicationsTemplatesListShadcn
            emailTemplates={templateType === 'email' ? allTemplates : []}
            smsTemplates={templateType === 'sms' ? allTemplates : []}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={setSelectedTemplateId}
            onCreateTemplate={handleCreateNewTemplate}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            templateType={templateType}
            onTemplateTypeChange={setTemplateType}
            showInactive={showInactive}
            onToggleInactive={setShowInactive}
            loading={settingsLoading || savingTemplates}
            canManageTemplates={true} // TODO: Add permission check based on user role
          />
        </div>

        {/* Right Panel - Template Editor (60% width) */}
        <div className="col-span-8 h-full min-h-0">
          <CommunicationsTemplateEditorShadcn
            template={selectedTemplate}
            templateType={templateType}
            isCreating={creatingMode && selectedTemplate?.isTemporary}
            onCreateTemplate={handleCreateRealTemplate}
            onTriggerChange={handleTriggerChange}
            onUpdateTemplate={handleUpdateTemplate}
            onDeleteTemplate={handleDeleteTemplate}
            onTestTemplate={handleTestTemplate}
            savingTemplate={savingTemplates}
            canEditTemplates={true} // TODO: Add permission check based on user role
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default CommunicationsSectionShadcn;